import type { PillTone } from '../../../ui'

export type IngestBatchStatus = 'queued' | 'processing' | 'review' | 'partial_failure' | 'failed' | 'completed'
export type IngestItemStatus = 'queued' | 'extracting' | 'summarising' | 'review' | 'failed' | 'confirmed'
export type ExtractionSource = 'pdfjs' | 'mixed' | 'ocr' | 'unreadable'

export interface IngestBatch {
    id: string
    created_by: string
    status: IngestBatchStatus
    total_items: number
    queued_items: number
    processing_items: number
    review_items: number
    failed_items: number
    confirmed_items: number
    created_at: string
    updated_at: string
}

export interface PageExtraction {
    page: number
    source: 'pdfjs' | 'ocr'
    chars: number
    error: string | null
}

/** A ranked suggestion from the identity scorer. Never a link until confirm-case succeeds. */
export interface CaseCandidate {
    claimRef: string
    score: number
    reasons: string[]
    methods: string[]
    clientName: string | null
    lenderName: string | null
    introducer: string | null
    preselected?: boolean
}

interface ValuedField {
    value: string | null
    confidence: number
    sourceSpan: string | null
}

/** comms.document_extract v1 output, as saved on the item. */
export interface ExtractResult {
    summary: string
    categoryKey: string
    categoryConfidence: number
    lenderName: ValuedField
    dates: { letterDate: ValuedField; deadlineDate: ValuedField }
    identifiers: { ourClaimRefs: string[]; proclaimRefs: string[]; lenderCaseRefs: string[]; agreementNumbers: string[] }
    client: Record<'firstName' | 'lastName' | 'dateOfBirth' | 'postcode' | 'addressLine' | 'email', ValuedField>
    vehicle: Record<'registration' | 'make' | 'model', ValuedField>
}

export interface IngestItem {
    id: string
    batch_id: string
    original_filename: string
    source_sha256: string
    mime_type: string
    size_bytes: number
    status: IngestItemStatus
    last_error: string | null
    attempt_count: number
    last_status_by: string | null
    last_status_at: string | null
    created_at: string
    extraction_source: ExtractionSource | null
    page_count: number | null
    pages_json: PageExtraction[] | null
    ocr_model: string | null
    extracted_at: string | null
    summary: string | null
    result_json: ExtractResult | null
    category_key: string | null
    category_label: string | null
    suggested_next_action: string | null
    next_action_label: string | null
    summarised_at: string | null
    candidates_json: CaseCandidate[] | null
    confirmed_claim_ref: string | null
    confirmed_by: string | null
    confirmed_at: string | null
    filed_introducer: string | null
    filed_pdf_key: string | null
    filed_extract_key: string | null
}

/** The `/item` detail also carries the saved text. */
export interface IngestItemDetail extends IngestItem {
    extracted_text: string | null
}

export interface IngestBatchDetail extends IngestBatch {
    items: IngestItem[]
}

export interface UploadRejection {
    filename: string
    reason: string
    duplicateOf?: string
}

export interface UploadResult {
    batchId: string | null
    accepted: { id: string; filename: string; source: 'direct' | 'zip' }[]
    rejected: UploadRejection[]
    processing: { started: boolean; reason?: string | null } | null
}

export interface ProcessingStatus {
    /** `leaseOwner` is null once the lease has expired, so a crashed run does not read as running. */
    job: { state: string; leaseOwner: string | null; finishedReason: string | null; lastError: string | null; startedAt: string | null; finishedAt: string | null } | null
    counts: Partial<Record<IngestItemStatus, number>>
}

export const IN_FLIGHT_STATUSES: IngestItemStatus[] = ['queued', 'extracting', 'summarising']
export const REPROCESSABLE_STATUSES: IngestItemStatus[] = ['review', 'failed']

export const isInFlight = (status: IngestItemStatus) => IN_FLIGHT_STATUSES.includes(status)

export const ITEM_STATUS: Record<IngestItemStatus, { label: string; tone: PillTone }> = {
    queued: { label: 'Queued', tone: 'neutral' },
    extracting: { label: 'Reading text', tone: 'info' },
    summarising: { label: 'Summarising', tone: 'info' },
    review: { label: 'Awaiting review', tone: 'warn' },
    failed: { label: 'Failed', tone: 'bad' },
    confirmed: { label: 'Filed', tone: 'good' },
}

export const BATCH_STATUS: Record<IngestBatchStatus, { label: string; tone: PillTone }> = {
    queued: { label: 'Queued', tone: 'neutral' },
    processing: { label: 'Processing', tone: 'info' },
    review: { label: 'Awaiting review', tone: 'warn' },
    partial_failure: { label: 'Some failed', tone: 'bad' },
    failed: { label: 'Failed', tone: 'bad' },
    completed: { label: 'All filed', tone: 'good' },
}

export const EXTRACTION_SOURCE: Record<ExtractionSource, { label: string; tone: PillTone; hint: string }> = {
    pdfjs: { label: 'pdf.js', tone: 'neutral', hint: 'Text read directly from the PDF' },
    mixed: { label: 'Mixed', tone: 'info', hint: 'Some pages read from the PDF, scanned pages read by OCR' },
    ocr: { label: 'OCR', tone: 'info', hint: 'Every page was scanned and read by OCR' },
    unreadable: { label: 'Unreadable', tone: 'bad', hint: 'No text could be read from this document' },
}

/** The batch is still moving, so the page keeps polling it. */
export const batchIsActive = (batch: Pick<IngestBatch, 'queued_items' | 'processing_items'>) => batch.queued_items + batch.processing_items > 0

export const formatBytes = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`)

export const PROCESSING_REASON: Record<string, string> = {
    pipeline_paused: 'Comms processing is paused (Pipeline settings), so these letters stay queued until it is resumed.',
    held_elsewhere: 'Processing is already running on another server; these letters will be picked up there.',
    already_running_here: 'Processing is already running; these letters join the queue.',
    worker_unavailable: 'The Comms worker is unavailable, so processing did not start. Try "Start processing" again shortly.',
    worker_required: 'Processing could not start on this server. Try "Start processing" again shortly.',
}
